import i18next, { type i18n as I18n } from 'i18next';

/** Jeziki, ki jih pozna `User.language` v zaledju (`accounts.LANGUAGE_CHOICES`). */
export const ACCOUNT_LANGUAGES = ['sl', 'en', 'de', 'it', 'hr'] as const;

/**
 * Ime jezika v tem jeziku — oznaka v izbirnikih jezika (Sysadmin → Jeziki,
 * profil). T2070: ključ `common.<koda>` za to ni uporaben, ker za `de`/`it`/`hr`
 * nosi ime DRŽAVE (izbirnik države v nastavitvah organizacije).
 */
export const LANGUAGE_NATIVE_NAMES: Record<string, string> = {
  sl: 'Slovenščina',
  en: 'English',
  de: 'Deutsch',
  it: 'Italiano',
  hr: 'Hrvatski',
};

/**
 * T2031 — jezik vmesnika, ki ga ob nastanku računa pošljemo zaledju.
 *
 * Vmesnik jezik brskalnika zazna sam (`i18next-browser-languagedetector`),
 * registracija pa ga prej ni pošiljala, zato je vsak nov račun dobil
 * slovenščino — nemški uporabnik je po registraciji v nemškem vmesniku dobival
 * slovensko pošto.
 *
 * Kot pri `jezikVmesnika()` v `services/esignApi.ts` (T1885): `resolvedLanguage`
 * (jezik, v katerem je vmesnik dejansko izrisan) in **primarni tag** (`de-DE` →
 * `de`). Nepodprt jezik da `en` (T2103 — tuj uporabnik dobi angleščino, ne
 * slovenščine); zaledje enako normalizira še enkrat.
 */
export function uiLanguage(): string {
  return primarniJezik(i18next);
}

function primarniJezik(instance: I18n): string {
  const koda = instance.resolvedLanguage || instance.language || '';
  const primarni = koda.toLowerCase().replace('_', '-').split('-')[0];
  return (ACCOUNT_LANGUAGES as readonly string[]).includes(primarni) ? primarni : 'en';
}

/**
 * T2046 — `<html lang>` sledi jeziku vmesnika (ob vezavi in ob vsaki menjavi).
 *
 * `index.html` je nosil fiksni `lang="en"` in nič ga ni posodabljalo, zato je
 * bralnik zaslona slovensko (ali nemško) besedilo bral z angleškim glasom;
 * `lang` določa tudi deljenje besed, črkovalnik in samodejni prevod brskalnika.
 * Vrednost je primarni tag jezika, v katerem je vmesnik izrisan (`sl-SI` →
 * `sl`) — isto pravilo kot `uiLanguage()`. Vrne funkcijo za odvezavo.
 */
export function bindDocumentLanguage(instance: I18n): () => void {
  const apply = () => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = primarniJezik(instance);
  };
  apply();
  instance.on('languageChanged', apply);
  return () => instance.off('languageChanged', apply);
}
